/**
 * Composant Performance Overview
 * Affiche les KPIs principaux avec évolutions et sparkline
 */

import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AnalyticsService,
  PerformanceOverview,
  PeriodType,
  TrendType,
} from '../../../../services/analytics.service';
import { LogService } from '../../../../../services/log.service';

@Component({
  selector: 'app-performance-overview',
  imports: [CommonModule],
  templateUrl: './performance-overview.component.html',
  styleUrls: ['./performance-overview.component.css'],
})
export class PerformanceOverviewComponent implements OnInit {
  private log = inject(LogService);
  private analyticsService = inject(AnalyticsService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  performanceData: PerformanceOverview | null = null;
  currentPeriod: PeriodType = 'today';
  isLoading = true;
  error: string | null = null;

  periods: { value: PeriodType; label: string }[] = [
    { value: 'today', label: "Aujourd'hui" },
    { value: 'week', label: 'Cette semaine' },
    { value: 'month', label: 'Ce mois' },
  ];

  ngOnInit(): void {
    this.loadPerformanceData();
  }

  selectPeriod(period: PeriodType): void {
    if (period !== this.currentPeriod) {
      this.currentPeriod = period;
      this.loadPerformanceData();
    }
  }

  private loadPerformanceData(): void {
    this.isLoading = true;
    this.error = null;

    this.analyticsService
      .getPerformanceOverview(this.currentPeriod)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.performanceData = data;
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.log.error('Erreur chargement performance:', err);
          this.error = 'Impossible de charger les statistiques';
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  getTrendIcon(trend: TrendType): string {
    switch (trend) {
      case 'up':
        return '↗️';
      case 'down':
        return '↘️';
      default:
        return '→';
    }
  }

  getTrendClass(trend: TrendType): string {
    switch (trend) {
      case 'up':
        return 'text-emerald-600';
      case 'down':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  }

  getTrendBgClass(trend: TrendType): string {
    switch (trend) {
      case 'up':
        return 'bg-emerald-50';
      case 'down':
        return 'bg-red-50';
      default:
        return 'bg-gray-50';
    }
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  formatPercentage(value: number): string {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  }

  // Calcul des points SVG pour la sparkline
  getSparklinePath(): string {
    if (!this.performanceData?.sparkline?.length) {
      return '';
    }

    const data = this.performanceData.sparkline;
    const width = 120;
    const height = 40;
    const padding = 4;

    const maxRevenue = Math.max(...data.map((d) => d.revenue));
    const minRevenue = Math.min(...data.map((d) => d.revenue));
    const range = maxRevenue - minRevenue || 1;

    const points = data.map((d, i) => {
      const x = padding + (i / (data.length - 1)) * (width - 2 * padding);
      const y =
        height -
        padding -
        ((d.revenue - minRevenue) / range) * (height - 2 * padding);
      return `${x},${y}`;
    });

    return `M ${points.join(' L ')}`;
  }

  getSparklineGradient(): string {
    if (!this.performanceData) return '';
    return this.performanceData.revenue.evolution.trend === 'up'
      ? 'url(#sparklineGradientUp)'
      : this.performanceData.revenue.evolution.trend === 'down'
        ? 'url(#sparklineGradientDown)'
        : 'url(#sparklineGradientStable)';
  }

  getPeriodLabel(): string {
    const period = this.periods.find((p) => p.value === this.currentPeriod);
    return period ? period.label.toLowerCase() : "aujourd'hui";
  }

  getComparisonLabel(): string {
    switch (this.currentPeriod) {
      case 'today':
        return 'vs hier';
      case 'week':
        return 'vs semaine dernière';
      case 'month':
        return 'vs mois dernier';
      default:
        return 'vs période précédente';
    }
  }
}
